import {
  EyeIcon,
  PencilSimpleIcon,
  TrashIcon,
  UserCheckIcon,
  UserMinusIcon,
} from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { type AdminUserSummary, useMe } from "@/lib/api";
import { type Role, roleLabel } from "@/lib/roles";
import { cn } from "@/lib/utils";

import { UserDeleteDialog } from "./user-delete-dialog";
import { UserDetailDialog } from "./user-detail-dialog";
import { UserEditDialog } from "./user-edit-dialog";
import { UserStatusDialog } from "./user-status-dialog";

const ROLE_BADGE_CLASSES: Record<Role, string> = {
  admin: "bg-accent-light text-accent-text",
  staff: "bg-primary/10 text-primary",
  client: "bg-muted/15 text-muted-foreground",
};

interface UserTableProps {
  users: AdminUserSummary[];
}

export function UserTable({ users }: UserTableProps) {
  const { data: me } = useMe();
  const [viewUserId, setViewUserId] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminUserSummary | null>(null);
  // The user stays set while the dialog closes, so its copy doesn't flip mid-animation.
  const [statusUser, setStatusUser] = useState<AdminUserSummary | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);

  if (users.length === 0) {
    return <p className="text-muted py-8 text-center text-sm">No users found.</p>;
  }

  const openStatusDialog = (user: AdminUserSummary) => {
    setStatusUser(user);
    setStatusOpen(true);
  };

  return (
    <>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/10 border-b">
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">Role</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Joined</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b last:border-0">
                <td className="px-4 py-3">
                  {user.first_name || user.last_name
                    ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                    : "—"}
                </td>
                <td className="px-4 py-3">{user.email}</td>
                <td className="px-4 py-3">
                  <span className={cn("nf-tag", ROLE_BADGE_CLASSES[user.role])}>
                    {roleLabel(user.role)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge disabled={!!user.disabled_at} />
                </td>
                <td className="text-muted px-4 py-3">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setViewUserId(user.id)}>
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setEditUserId(user.id)}>
                      <PencilSimpleIcon className="h-4 w-4" />
                    </Button>
                    <StatusActionButton
                      user={user}
                      isSelf={me?.id === user.id}
                      onClick={() => openStatusDialog(user)}
                    />
                    <Button variant="ghost" size="icon" onClick={() => setDeleteUser(user)}>
                      <TrashIcon className="text-destructive h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDetailDialog
        userId={viewUserId}
        open={!!viewUserId}
        onOpenChange={(open) => !open && setViewUserId(null)}
      />
      <UserEditDialog
        userId={editUserId}
        open={!!editUserId}
        onOpenChange={(open) => !open && setEditUserId(null)}
      />
      <UserDeleteDialog
        user={deleteUser}
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
      />
      <UserStatusDialog
        key={statusUser?.id}
        user={statusUser}
        open={statusOpen}
        onOpenChange={setStatusOpen}
      />
    </>
  );
}

interface StatusBadgeProps {
  disabled: boolean;
}

function StatusBadge({ disabled }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        "nf-tag",
        disabled ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success",
      )}
    >
      {disabled ? "Deactivated" : "Active"}
    </span>
  );
}

interface StatusActionButtonProps {
  user: AdminUserSummary;
  /** The signed-in admin's own row: an admin can't deactivate themselves. */
  isSelf: boolean;
  onClick: () => void;
}

function StatusActionButton({ user, isSelf, onClick }: StatusActionButtonProps) {
  const action = user.disabled_at ? "Reactivate" : "Deactivate";
  return (
    <Button
      variant="ghost"
      size="icon"
      disabled={isSelf}
      onClick={onClick}
      aria-label={`${action} ${user.email}`}
      title={isSelf ? "You can't deactivate your own account" : action}
    >
      {user.disabled_at ? (
        <UserCheckIcon className="h-4 w-4" />
      ) : (
        <UserMinusIcon className="h-4 w-4" />
      )}
    </Button>
  );
}
